import { ref, computed } from 'vue';
import { getAll } from '@/pages/Bridges/RebootAllBridgeCameras/Helpers/RequestHelpers.ts';
import { ApiSwitchWithIncludes } from '@eencloud/eewc-components/src/service/api-types';
import api from '@eencloud/eewc-components/src/service/api.ts';
import { defineStore } from 'pinia';
import { useUsersStore } from '@/stores';

export type SwitchPort = {
  deviceId: string;
  switchId: string;
  portId: number;
};

export const useSwitchPortsStore = defineStore('Rebooter - SwitchPortsStorage', () => {
  const switchPorts = ref<SwitchPort[]>([]);
  const usersStore = useUsersStore();

  const hasUserRebootPermission = computed(() => {
    return usersStore?.currentUser?.permissions?.addEditBridgesCameras;
  });

  const prefetchSwitchPorts = async () => {
    try {
      const response = await getAll<ApiSwitchWithIncludes>(async (nextPageToken) => {
        return await api.getSwitchesOrError({
          pageSize: 100,
          include: ['ports'].join(','),
          ...(nextPageToken ? { pageToken: nextPageToken } : {}),
        });
      }, 3);

      if (!response || !response.length) {
        return [];
      }

      const extractedSwitchPorts = response.flatMap(getSwitchPortsFromAPIResponse);
      switchPorts.value = extractedSwitchPorts;

      // log stora usage by switch ports
      if (window.useSwitchPortsStorageDebugger.showStorageUsage) {
        const switchPortsSize = new Blob([JSON.stringify(extractedSwitchPorts)]).size;
        const switchPortsSizeInMB = (switchPortsSize / 1024 / 1024).toFixed(2);
        console.log(`[useSwitchPortsStorage] Storage usage by switch ports: ${switchPortsSizeInMB} MB`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getSwitchPortsFromAPIResponse = (switchData: ApiSwitchWithIncludes): SwitchPort[] => {
    const showUnexpectedAPIResponse = window.useSwitchPortsStorageDebugger?.showWarningOnUnexpectedAPIResponse;
    const ports = switchData?.ports;

    if (!ports) {
      showUnexpectedAPIResponse &&
        console.warn('[useSwitchPortsStorage] Warning: Got switch without ports', switchData);
      return [];
    }

    return ports.flatMap((port) => {
      if (!port.id) {
        showUnexpectedAPIResponse && console.warn('[useSwitchPortsStorage] Warning: Got port without id', switchData);
        return [];
      }

      if (!port.devices) {
        return [];
      }

      return port.devices.flatMap((attachedDevice): SwitchPort[] => {
        if (!port.id) {
          showUnexpectedAPIResponse && console.warn('[useSwitchPortsStorage] Warning: Got port without id', switchData);
          return [];
        }

        if (!attachedDevice.esn) {
          showUnexpectedAPIResponse &&
            console.warn('[useSwitchPortsStorage] Warning: Got device without esn', switchData, attachedDevice);
          return [];
        }

        return [
          {
            deviceId: attachedDevice.esn,
            portId: port.id,
            switchId: switchData.id,
          },
        ];
      });
    });
  };

  return {
    switchPorts,
    prefetchSwitchPorts,
    hasUserRebootPermission,
    getSwitchPortsFromAPIResponse,
  };
});

declare global {
  interface Window {
    useSwitchPortsStorageDebugger: {
      showWarningOnUnexpectedAPIResponse: boolean;
      showStorageUsage: boolean;
    };
  }
}

window.useSwitchPortsStorageDebugger = window.useSwitchPortsStorageDebugger || {
  showWarningOnUnexpectedAPIResponse: false,
  showStorageUsage: false,
};
